import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import V1GroupJoined from "../../payloads/incoming/v1-group-joined.ts";
import UiUtils from "../../utils/ui-utils.ts";
import Route from "../../enums/route.ts";
import LogUtils from "../../utils/log-utils.ts";

export default class JoinGroupScreen extends BaseUI {
  constructor(state: State, apiClient: EatOutApiClient, navigateCallback: (route: Route) => void) {
    super("join-group-screen", state, apiClient, navigateCallback);

    // subscribe to events
    this.apiClient.v1GroupJoinedCallback = this.handleV1GroupJoined.bind(this);
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // create form
    const form: HTMLFormElement = UiUtils.form(this.handleJoinGroupFormSubmitEvent.bind(this));
    newElements.push(form);

    // create group code input
    const joinLabel: HTMLLabelElement = UiUtils.label("Enter group code ", "code");
    form.appendChild(joinLabel);
    const codeInput: HTMLInputElement = UiUtils.inputNumber("code", 0, 999999, "one-time-code");
    form.appendChild(codeInput);

    // if the query parameters contain a group code, autofill it inside group code input
    try {
      const urlSearchParams: URLSearchParams = (new URL(document.location.toString())).searchParams;
      const groupCode: string | null = urlSearchParams.get("groupcode");
      if (groupCode !== null) {
        codeInput.value = groupCode;
      }

      // now wipe all query parameters away
      globalThis.history.pushState({}, document.title, "/app");
    } catch (error) {
      LogUtils.error(`Failed to check query parameters for Group Code: ${error}`);
    }

    // create join button
    const joinButton: HTMLButtonElement = UiUtils.button("Join");
    form.appendChild(joinButton);

    // back button
    const backButton: HTMLButtonElement = UiUtils.button("Back", () => this.navigateCallback(Route.MAIN_MENU));
    newElements.push(backButton);

    return super.render(newElements);
  }

  private handleJoinGroupFormSubmitEvent(event: SubmitEvent): void {
    // make sure the event's target was truly an HTMLFormElement
    const eventTarget: EventTarget | null = event.target;
    if (!(eventTarget instanceof HTMLFormElement)) {
      LogUtils.error(
        `attempted to retrieve group code from Join Group form, but the event's target was a ${typeof eventTarget} when we expected an HTMLFormElement`,
      );
    }

    // get group code from form
    const formData: FormData = new FormData(eventTarget);
    if (!formData.has("code")) {
      LogUtils.error("attempted to get group code from Join Group form, but it wasn't available");
    }
    const groupCode: FormDataEntryValue | null = formData.get("code");
    if (typeof groupCode !== "string") {
      LogUtils.error(
        `attempted to retrieve group code from Join Group form, but it was a ${typeof groupCode} when we expected a string`,
      );
    }

    // perform the action we want to do next
    this.apiClient.sendJoinGroupPayload(groupCode);
  }

  private handleV1GroupJoined(v1GroupJoined: V1GroupJoined): void {
    // the logged-in user should not be the group's owner
    if (v1GroupJoined.payload.group.ownerId === this.state.userState.id) {
      LogUtils.error(
        "received V1GroupJoined payload within JoinGroup screen, but the logged-in user is the group owner",
      );
    }

    // make sure that we're the user that joined the group
    if (v1GroupJoined.payload.user.id !== this.state.userState.id) {
      LogUtils.error(
        "received V1GroupJoined payload within JoinGroup screen, but the payload wasn't about the logged-in user",
      );
    }

    // store the state of the Group we've just joined,
    // and mark it as the 'current group'
    this.state.groupState.addGroup(v1GroupJoined.payload.group);
    this.state.groupState.currentGroupId = v1GroupJoined.payload.group.id;

    // now that we've joined the group, switch to restaurant categories screen
    LogUtils.debug(this.debug, "Successfully joined a group.");
    this.navigateCallback(Route.RESTAURANT_CATEGORIES);
  }
}
